import { useState } from 'react';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';

import validationSchema from './form-modal/validation-schema';

import FungibleTokens, {
  fungibleTokensService
} from 'services/fungible-tokens';
const { getParsedTokenAmount } = FungibleTokens;

import RenderStepContent from './components/render-step-content';
import Buttons from './components/buttons';
import CloseIcon from 'components/icons/CloseIcon';

import { useAppSelector, useAppDispatch } from 'redux/use-hooks';

import {
  selectActiveTab,
  selectActiveToken,
  actions as tokensSliceActions
} from 'redux/slices/tokens';

const { setActiveTab, fetchTokens } = tokensSliceActions;

import { actionAccountSetStorageBalance, selectAccountId } from 'redux/slices/account';
import { refreshAccount } from 'redux/actions/account';
import { actions as statusSliceActions } from 'redux/slices/status';
import { getWalletActionErrorPresentation } from 'utils/wallet';
const { clearLocalAlert, showCustomAlert } = statusSliceActions;

import { OptionType, FormValues } from 'types';
import { VIEWS } from 'lib/constants';
import { getStep } from 'utils';
import config from 'config';

import { StyledContainer, StyledSection } from '../views.style';
import { StyledCloseButton } from './send-view.style';

const { OCC_CONTRACT } = config;

const SendTabsView = ({ closeModal }: { closeModal: () => void }) => {
  const [activeStep, setActiveStep] = useState(VIEWS.ENTER_AMOUNT);
  const [sendingToken, setSendingToken] = useState(false);
  const [estimatedTotalFees, setEstimatedTotalFees] = useState('0');
  const [estimatedTotalInNear, setEstimatedTotalInNear] = useState('0');
  const currentValidationSchema = validationSchema[activeStep];
  const isLastStep: boolean = activeStep === VIEWS.REVIEW;

  const dispatch = useAppDispatch();
  const accountId = useAppSelector(selectAccountId);
  const activeToken = useAppSelector(selectActiveToken);
  const activeTab = useAppSelector(selectActiveTab);
  const setDispatchActiveTab = (item: OptionType) =>
    dispatch(setActiveTab(item));

  const initialValues = {
    amount: '',
    selected: [],
    receiverId: '',
    token: activeToken !== null ? activeToken?.onChainFTMetadata?.symbol : 'OPN'
  };

  const getRawAmount = (amount) =>
    getParsedTokenAmount(
      amount,
      activeToken.onChainFTMetadata?.symbol,
      activeToken.onChainFTMetadata?.decimals
    );

  const handleBack = () => setActiveStep((step) => getStep(step, -1, VIEWS));

  const submitHandler = async (
    values: FormValues,
    { touched, validateForm, setTouched, setSubmitting }: any
  ) => {
    const errs = await validateForm();
    if (isEmpty(errs)) {
      if (isLastStep) {

        setSendingToken(true);
        const isStorageBalance =activeToken.contractName?
            await fungibleTokensService.isStorageBalanceAvailable({
              contractName: activeToken.contractName,
              accountId:values.receiverId
            }):true;
        try {
          if(!isStorageBalance) {
            await fungibleTokensService.transferAndStorage({
              accountId,
              amount: getRawAmount(values.amount),
              selected: values.selected,
              receiverId: values.receiverId,
              contractName: activeToken.contractName
            });
          }else {
            await fungibleTokensService.transfer({
              accountId,
              amount: getRawAmount(values.amount),
              selected: values.selected,
              receiverId: values.receiverId,
              contractName: activeToken.contractName
            });
          }

          setSendingToken(false);
          setSubmitting(false);
          setActiveStep(VIEWS.SUCCESS);
          dispatch(clearLocalAlert());
          dispatch(refreshAccount());
          dispatch(fetchTokens({ accountId }));
          dispatch(actionAccountSetStorageBalance({ contractName: OCC_CONTRACT, accountId }));
        } catch (e: any) {
          const presentation = getWalletActionErrorPresentation(e);
          dispatch(
            showCustomAlert({
              showAlert: true,
              success: false,
              messageCodeHeader: 'error',
              messageCode: presentation.messageCode,
              errorMessage: presentation.errorMessage
            })
          );
          setSendingToken(false);
          setSubmitting(false);
        }
      } else {
        setActiveStep((step) => getStep(step, +1, VIEWS));

        if (activeStep === VIEWS.ENTER_RECEIVER) {
          try {
            if (activeToken.onChainFTMetadata?.symbol === 'NEAR') {
              const [totalFees, totalNear] = await Promise.all([
                fungibleTokensService.getEstimatedTotalFees(),
                fungibleTokensService.getEstimatedTotalNearAmount({
                  amount: getRawAmount(values.amount)
                })
              ]);
              setEstimatedTotalFees(totalFees);
              setEstimatedTotalInNear(totalNear);
            } else {
              const totalFees =
                await fungibleTokensService.getEstimatedTotalFees({
                  accountId: values.receiverId,
                  contractName: activeToken.contractName
                });
              setEstimatedTotalFees(totalFees);
            }
          } catch (e) {
            dispatch(
              showCustomAlert({
                showAlert: true,
                success: false,
                messageCodeHeader: 'error',
                errorMessage: e.message
              })
            );
          }
        }
        setSubmitting(false);
      }
    } else {
      setTouched({ ...touched, ...errs });
    }
  };

  return (
    <StyledContainer>
      <StyledCloseButton onClick={closeModal}>
        <CloseIcon color="#222" />
      </StyledCloseButton>
      <StyledSection p="48px">
        <Formik
          validateOnBlur={false}
          initialValues={initialValues}
          validationSchema={currentValidationSchema(activeToken)}
          onSubmit={submitHandler}
        >
          {() => {
            return (
              <Form>
                <RenderStepContent
                  step={activeStep}
                  stepTab={[activeTab, setDispatchActiveTab]}
                  closeModal={closeModal}
                  estimatedTotalFees={estimatedTotalFees}
                  estimatedTotalInNear={estimatedTotalInNear}
                />
                {activeTab.value === 'send' && activeStep !== VIEWS.SUCCESS && (
                  <Buttons
                    loading={sendingToken}
                    step={activeStep}
                    isLastStep={isLastStep}
                    onClickBackHandler={handleBack}
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      </StyledSection>
    </StyledContainer>
  );
};

export default SendTabsView;
